class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('#predictive-search');
    this.empty = this.querySelector('#empty_search');
    if (!this.input || !this.results) return;
    this.initialized = true;
    this.sequence = 0;
    this.input.addEventListener('input', () => {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence++;
      if (!this.input.value.trim()) return this.reset();
      this.timer = setTimeout(() => this.load(), 180);
    });
    this.input.addEventListener('focus', () => {
      if (!this.input.value.trim()) this.showHistory();
    });
    this.querySelector('.nav-search-clearText')?.addEventListener('click', () => {
      this.input.value = '';
      this.reset();
      this.input.focus();
    });
    this.querySelector('form')?.addEventListener('submit', event => {
      const term = this.input.value.trim();
      if (!term) return event.preventDefault();
      this.input.value = term;
      this.remember(term);
    });
    this.addEventListener('keydown', event => {
      const links = [...this.results.querySelectorAll('a[href]')];
      const index = links.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' && links.length) {
        event.preventDefault();
        links[(index + 1) % links.length].focus();
      } else if (event.key === 'ArrowUp' && index >= 0) {
        event.preventDefault();
        if (index === 0) this.input.focus();
        else links[index - 1].focus();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.querySelector('.js-searchToggle--close')?.click();
      }
    });
  }
  reset() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.sequence++;
    this.results.replaceChildren();
    this.results.removeAttribute('aria-busy');
    this.results.style.removeProperty('display');
    this.results.classList.remove('active');
    this.empty?.replaceChildren();
    this.input.setAttribute('aria-expanded', 'false');
  }
  async load() {
    const term = this.input.value.trim();
    const sequence = this.sequence;
    if (!term) return this.reset();
    const controller = new AbortController();
    this.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 8000);
    const loading = document.createElement('p');
    loading.className = 'native-search-status';
    loading.textContent = this.dataset.loading;
    this.results.replaceChildren(loading, this.searchLink(term, this.dataset.viewAll));
    this.results.style.removeProperty('display');
    this.results.classList.remove('active');
    this.input.setAttribute('aria-expanded', 'true');
    this.results.setAttribute('aria-busy', 'true');
    const url = new URL(this.dataset.suggestUrl, location.origin);
    url.search = new URLSearchParams({
      q: term,
      section_id: 'native-search-suggestions',
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'hide',
      'resources[options][fields]': 'title,product_type,variants.title,variants.sku'
    }).toString();
    try {
      const response = await fetch(url, {signal: controller.signal});
      if (!response.ok) throw new Error('Search unavailable');
      const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = documentResult.querySelector('[data-native-suggestions]');
      if (!content) throw new Error('Search response missing');
      if (sequence !== this.sequence || term !== this.input.value.trim()) return;
      this.results.replaceChildren(content);
      this.results.style.removeProperty('display');
      this.results.classList.remove('active');
      this.input.setAttribute('aria-expanded', 'true');
    } catch (error) {
      if (sequence !== this.sequence) return;
      this.results.replaceChildren();
      const message = document.createElement('p');
      message.textContent = this.dataset.error;
      this.results.append(message, this.searchLink(term, this.dataset.viewAll));
      this.input.setAttribute('aria-expanded', 'true');
    } finally {
      clearTimeout(timeout);
      if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
    }
  }
  searchLink(term, text) {
    const link = document.createElement('a');
    const url = new URL(this.querySelector('form').action);
    url.search = new URLSearchParams({q:term, type:'product', 'options[prefix]':'last'}).toString();
    link.href = url.href;
    link.textContent = text;
    link.className = 'native-search-all';
    return link;
  }
  history() {
    try {
      const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
      return Array.isArray(history) ? history.filter(x => typeof x === 'string' && x.length <= 250).slice(0, 3) : [];
    } catch { return []; }
  }
  remember(term) {
    try { localStorage.setItem('searchHistory', JSON.stringify([term, ...this.history().filter(x => x !== term)].slice(0, 3))); } catch {}
  }
  showHistory() {
    const history = this.history();
    if (!history.length) return;
    const list = document.createElement('ul');
    list.className = 'native-search-history';
    list.setAttribute('aria-label', this.dataset.recent);
    for (const term of history) {
      const item = document.createElement('li');
      item.append(this.searchLink(term, term));
      list.append(item);
    }
    this.results.replaceChildren(list);
    this.input.setAttribute('aria-expanded', 'true');
  }
}
if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
